import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ShieldCheck, FileText, Check, X, Eye, Store } from "lucide-react";
import { shortDateTime } from "@/lib/format";

type Merchant = {
  id: string; name: string; business_name?: string; category: string;
  active: boolean; lifecycle_status: string | null;
  contact_phone: string | null; contact_email: string | null;
  created_at: string; owner_id: string;
};
type Doc = {
  id: string; merchant_id: string; doc_type: string; doc_number: string | null;
  storage_path: string | null; status: string; reviewer_note: string | null;
  submitted_at: string | null; reviewed_at: string | null;
  file_name?: string | null; file_size?: number | null; mime_type?: string | null;
};

export function MerchantApprovalsPanel({ schoolId }: { schoolId: string }) {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<Merchant | null>(null);
  const [note, setNote] = useState("");

  const { data: pending = [] } = useQuery<Merchant[]>({
    queryKey: ["merchants-pending", schoolId],
    queryFn: async () => (await supabase.from("merchants")
      .select("id,name,category,active,lifecycle_status,contact_phone,contact_email,created_at,owner_id")
      .eq("school_id", schoolId)
      .in("lifecycle_status", ["pending_review", "rejected"])
      .order("created_at", { ascending: false })).data as unknown as Merchant[] ?? [],
  });

  const { data: docs = [] } = useQuery<Doc[]>({
    queryKey: ["merchant-docs", selected?.id],
    enabled: !!selected,
    queryFn: async () => (await supabase.from("merchant_documents" as never).select("*").eq("merchant_id", selected!.id).order("submitted_at", { ascending: false })).data as unknown as Doc[] ?? [],
  });

  const decide = useMutation({
    mutationFn: async (approve: boolean) => {
      const { error } = await (supabase.rpc as unknown as (fn: string, a: Record<string, unknown>) => Promise<{ error: { message: string } | null }>)("approve_merchant", {
        _merchant_id: selected!.id, _approve: approve, _note: note || null,
      });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      toast.success("Decision recorded — merchant & audit updated");
      qc.invalidateQueries({ queryKey: ["merchants-pending", schoolId] });
      setSelected(null); setNote("");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="space-y-4">
      <Card className="p-5 bg-gradient-to-br from-amber-50/60 via-card to-card">
        <div className="flex items-start justify-between gap-4">
          <div>
            <Badge variant="secondary" className="gap-1"><ShieldCheck className="h-3 w-3" />KYC review queue</Badge>
            <h2 className="mt-2 font-display text-2xl">Merchant approvals</h2>
            <p className="text-sm text-muted-foreground">Merchants stay inactive until you review their documents and approve.</p>
          </div>
          <div className="text-right">
            <div className="text-xs text-muted-foreground">Pending</div>
            <div className="font-display text-3xl">{pending.filter(p => p.lifecycle_status === "pending_review").length}</div>
          </div>
        </div>
      </Card>

      <Card className="p-4">
        {pending.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground">No merchants awaiting review.</div>}
        <div className="divide-y">
          {pending.map((m) => (
            <div key={m.id} className="flex items-center justify-between gap-3 py-3">
              <div className="flex items-center gap-3">
                <div className="grid h-10 w-10 place-items-center rounded-lg bg-primary/10 text-primary"><Store className="h-5 w-5" /></div>
                <div>
                  <div className="font-medium">{m.business_name || m.name}</div>
                  <div className="text-xs text-muted-foreground capitalize">{m.category} · {m.contact_phone ?? m.contact_email ?? "no contact"}</div>
                  <div className="text-[11px] text-muted-foreground">Submitted {shortDateTime(m.created_at)}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant={m.lifecycle_status === "rejected" ? "destructive" : "secondary"} className="capitalize">{m.lifecycle_status?.replace(/_/g, " ")}</Badge>
                <Button size="sm" variant="outline" onClick={() => { setSelected(m); setNote(""); }}><Eye className="mr-1 h-3 w-3" />Review</Button>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) setSelected(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Review · {selected?.business_name || selected?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <Row k="Category" v={selected?.category ?? ""} />
              <Row k="Contact" v={selected?.contact_phone || selected?.contact_email || "—"} />
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Uploaded documents</div>
              <div className="rounded-lg border divide-y">
                {docs.length === 0 && <div className="p-3 text-xs text-muted-foreground">No documents uploaded.</div>}
                {docs.map((d) => (
                  <div key={d.id} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText className="h-4 w-4 text-primary shrink-0" />
                      <span className="truncate">{d.file_name ?? d.doc_type}</span>
                      <Badge variant="outline" className="text-[10px]">{d.doc_type}</Badge>
                      {d.doc_number && <span className="font-mono text-[10px] text-muted-foreground">{d.doc_number}</span>}
                    </div>
                    <div className="flex items-center gap-2">
                      {d.storage_path && (
                        <a href={d.storage_path} target="_blank" rel="noreferrer" className="text-xs text-primary underline">Open</a>
                      )}
                      <Badge className="text-[10px] capitalize">{d.status}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Reviewer note</div>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional — shown to merchant if rejected." rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => decide.mutate(false)} disabled={decide.isPending}><X className="mr-1 h-4 w-4" />Reject</Button>
            <Button onClick={() => decide.mutate(true)} disabled={decide.isPending}><Check className="mr-1 h-4 w-4" />Approve & activate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (<div className="rounded-md border p-2"><div className="text-[10px] uppercase text-muted-foreground">{k}</div><div className="font-medium truncate">{v}</div></div>);
}
